/**
 * Executes a {@link CreateCvPlan}. Platform-agnostic on purpose: it performs the
 * API calls and returns the id of the CV that was created, and the calling
 * screen decides how to navigate there.
 *
 * Every branch maps onto an endpoint that already exists; nothing here is a new
 * write path. Which mode a skill run uses is decided by the machine and arrives
 * as `plan.writeMode`: a CV the user PICKED is protected with `new_version` so
 * the run writes a sibling and the original is never overwritten - which is what
 * "create a new CV from an existing one" has to mean. A CV this flow made for
 * the run (an imported resume, or the profile copy materialized below) has
 * nothing to protect and is updated in place, because a sibling there would just
 * leave the intermediate behind as a duplicate.
 *
 * Submitting is NOT atomic for an upload: the resume becomes a CV, then the
 * tailoring runs on it. `onCvImported` reports that first id so the caller can
 * resume from the tailoring if it fails, instead of re-importing on every press.
 */
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ParsedCvData } from '@tabbio/types';
import {
  createGeneratedTailoredCV,
  createOrUpdateCvHandoffDraft,
  createTailoredCV,
  createTailoredCvFromParsed,
  deleteTailoredCV,
  duplicateTailoredCV,
  updateCV,
  updateCVOverrides,
} from '../../../lib/tailored-cv-api';
import {
  isConfirmationRequired,
  getSkillRunByIdempotencyKey,
  prepareSkillRunFor,
  runSkill,
  SkillsApiError,
  type RunSkillInput,
} from '../../../lib/skills-api';
import { MASTER_CV_ID } from '../../../lib/cv-main-default';
import {
  planAfterImport,
  resolveCreateCvPlan,
  type CreateCvPlan,
  type CreateCvState,
} from './create-cv-machine';

export interface CreateCvHandoff {
  recipientName: string;
  recipientEmail: string;
  mode: 'copy' | 'transfer';
}

export interface CreateCvSubmitInput {
  state: CreateCvState;
  /** Resolved title, already defaulted by the caller. */
  title: string;
  /** Present only when the source is an uploaded resume. */
  parsed?: ParsedCvData | null;
  importEvidenceId?: string;
  handoff?: CreateCvHandoff | null;
  /**
   * Answers to the skill's own declared run-screen fields, keyed by field id.
   * Required fields left out here are exactly what the API refuses with
   * SKILL_INPUT_REQUIRED, so the flow collects them before submitting.
   */
  skillInputs?: Record<string, unknown>;
  /**
   * Called the moment an uploaded resume has become a CV, BEFORE the tailoring
   * that follows can fail. The caller records the id so a retry resumes from
   * the tailoring instead of importing the same resume all over again.
   */
  onCvImported?: (cvId: string) => void;
}

export interface CreateCvSubmitResult {
  cvId: string;
  /** The plan that produced it, for analytics + result copy. */
  plan: CreateCvPlan;
}

const SKILL_RECONCILE_ATTEMPTS = 8;
const SKILL_RECONCILE_DELAY_MS = 750;

class SkillRunOutcomeUnknownError extends Error {
  readonly outcomeUnknown = true;
}

function createSkillRunIdempotencyKey() {
  const uuid = globalThis.crypto?.randomUUID?.();
  const suffix =
    uuid ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `create-cv-${suffix}`;
}

export function isUncertainSkillRunError(error: unknown) {
  const code = error instanceof SkillsApiError ? error.code : '';
  if (
    ['NETWORK_ERROR', 'HTTP_408', 'HTTP_502', 'HTTP_503', 'HTTP_504'].includes(
      code,
    )
  ) {
    return true;
  }
  const message = error instanceof Error ? error.message : String(error);
  return /network|timed? out|timeout|gateway|abort|maximum duration|request (?:time|duration).*exceed/i.test(
    message,
  );
}

async function reconcileSkillRun(idempotencyKey: string) {
  for (let attempt = 0; attempt < SKILL_RECONCILE_ATTEMPTS; attempt += 1) {
    try {
      const run = await getSkillRunByIdempotencyKey(idempotencyKey);
      if (run.status === 'completed') return run;
      if (run.status === 'failed' || run.status === 'blocked') {
        throw new SkillsApiError(
          'The CV skill did not complete.',
          run.errorCode || 'SKILL_RUN_FAILED',
        );
      }
    } catch (error) {
      const code = error instanceof SkillsApiError ? error.code : '';
      const notReady =
        code === 'SKILL_RUN_NOT_FOUND' ||
        code === 'HTTP_404' ||
        isUncertainSkillRunError(error);
      if (!notReady) throw error;
    }

    if (attempt < SKILL_RECONCILE_ATTEMPTS - 1) {
      await new Promise((resolve) =>
        setTimeout(resolve, SKILL_RECONCILE_DELAY_MS),
      );
    }
  }
  return null;
}

async function runSkillWithReconciliation(
  skillId: string,
  input: RunSkillInput,
) {
  try {
    const result = await runSkill(skillId, input);
    if (result.status === 'completed') return result;
    const reconciled = input.idempotencyKey
      ? await reconcileSkillRun(input.idempotencyKey)
      : null;
    if (reconciled) {
      return {
        runId: reconciled.id,
        status: reconciled.status,
        run: reconciled,
        diff: reconciled.diff,
      };
    }
    throw new SkillRunOutcomeUnknownError(
      'The skill is still finishing. Your CV was kept and will appear when the run completes.',
    );
  } catch (error) {
    if (!isUncertainSkillRunError(error) || !input.idempotencyKey) throw error;
    const reconciled = await reconcileSkillRun(input.idempotencyKey);
    if (reconciled) {
      return {
        runId: reconciled.id,
        status: reconciled.status,
        run: reconciled,
        diff: reconciled.diff,
      };
    }
    throw new SkillRunOutcomeUnknownError(
      'The skill is still finishing. Your CV was kept and will appear when the run completes.',
    );
  }
}

export function useCreateCvSubmit() {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Set while an AI plan is running, so the screen can show the right copy. */
  const [isGenerating, setIsGenerating] = useState(false);

  const runPlan = useCallback(
    async (
      plan: CreateCvPlan,
      input: CreateCvSubmitInput,
    ): Promise<string> => {
      const { title, parsed } = input;

      switch (plan.kind) {
        case 'import': {
          if (!parsed) throw new Error('No parsed resume to import');
          // One call, server-side: `POST /api/cv/from-parsed` runs the shared
          // `parsedCvToTailoredOverrides` converter and writes title, summary
          // and the full section snapshot in a single row create. The CV stays
          // isolated from the master profile, and the parsed resume no longer
          // has to survive `tailoredCvOverridesPatchSchema` - whose closed
          // language allow-list and name-character refine rejected ordinary
          // resumes and surfaced a raw ZodError as the failure message.
          const cv = await createTailoredCvFromParsed({ parsed, title, importEvidenceId: input.importEvidenceId });
          return cv.id;
        }

        case 'reuse': {
          // The import already wrote the content. Only the name can still have
          // changed since - the user can rename on step 1 and come back - so
          // carry the resolved title across rather than silently keeping the
          // one the import happened to derive.
          await updateCV(plan.cvId, { title }).catch(() => undefined);
          return plan.cvId;
        }

        case 'blank': {
          const cv = await createTailoredCV({ title });
          // Start blank must not inherit the master profile summary: persist an
          // explicit empty summary so the resolver renders a blank one.
          await updateCV(cv.id, { summary: '' });
          await updateCVOverrides(cv.id, {
            header:
              input.handoff?.recipientName.trim() ||
              input.handoff?.recipientEmail.trim()
                ? {
                    name: input.handoff.recipientName.trim() || null,
                    contactEmail: input.handoff.recipientEmail.trim() || null,
                  }
                : null,
            experiences: [],
            educations: [],
            certifications: [],
            highlights: [],
            projects: [],
            skillGroups: [],
            languages: [],
          });
          return cv.id;
        }

        case 'profile': {
          // No overrides: the resolver fills every section from the master
          // profile, which is exactly "start from my profile".
          const cv = await createTailoredCV({ title });
          return cv.id;
        }

        case 'duplicate': {
          const cv = await duplicateTailoredCV({
            cvId: plan.sourceCvId,
            title,
          });
          return cv.id;
        }

        case 'generate': {
          setIsGenerating(true);
          try {
            const cv = await createGeneratedTailoredCV({
              title,
              prompt: plan.prompt,
              sourceCvId:
                plan.sourceCvId === MASTER_CV_ID ? null : plan.sourceCvId,
            });
            return cv.id;
          } finally {
            setIsGenerating(false);
          }
        }

        case 'skill_run': {
          setIsGenerating(true);
          // A skill can only write into a concrete CV. Starting from the
          // master profile there is none yet, so materialize the profile CV
          // first (the same resolver-backed create as the `profile` plan) and
          // let the skill update it in place: that fresh copy IS the
          // deliverable, unlike a picked source CV, which stays safe behind
          // `mode: 'new_version'`.
          const materializedCvId =
            plan.cvId === MASTER_CV_ID
              ? (await duplicateTailoredCV({ cvId: MASTER_CV_ID, title })).id
              : !plan.cvId
                ? (await createTailoredCV({ title })).id
                : null;
          try {
            const inputs: Record<string, unknown> = { ...input.skillInputs };
            // A v2 skill declares its own job-post field and answers it above;
            // `jobDescription` is the fallback key for everything else, and the
            // service reads both when building the prompt.
            if (plan.jobDescription) inputs.jobDescription ??= plan.jobDescription;
            const sourceCvId = materializedCvId ?? plan.cvId!;
            const runInput: RunSkillInput = {
              cvId: sourceCvId,
              inputs,
              dryRun: false,
              mode: plan.writeMode,
              title,
              idempotencyKey: createSkillRunIdempotencyKey(),
            };
            const result = await runSkillWithReconciliation(plan.skillId, runInput).catch(
              async (error) => {
                // Full-CV writes need a 10-minute confirmation token. The user
                // already confirmed by submitting this flow, so mint one and
                // retry rather than bouncing them into a second dialog.
                if (isConfirmationRequired(error)) {
                  // Bound to this exact write: CV, mode and title (B09).
                  const prepared = await prepareSkillRunFor(plan.skillId, {
                    ...runInput,
                    cvId: sourceCvId,
                  });
                  return runSkillWithReconciliation(plan.skillId, {
                    ...runInput,
                    confirmationToken: prepared.confirmationToken,
                  });
                }
                throw error;
              },
            );
            return result.run?.resultCvId ?? sourceCvId;
          } catch (error) {
            // A failed run must not leave the half-made profile copy behind.
            if (
              materializedCvId &&
              !(error instanceof SkillRunOutcomeUnknownError)
            ) {
              await deleteTailoredCV(materializedCvId).catch(() => undefined);
            }
            throw error;
          } finally {
            setIsGenerating(false);
          }
        }
      }
    },
    [],
  );

  const submit = useCallback(
    async (input: CreateCvSubmitInput): Promise<CreateCvSubmitResult> => {
      setIsSubmitting(true);
      try {
        const firstPlan = resolveCreateCvPlan(input.state);
        let cvId = await runPlan(firstPlan, input);
        let plan = firstPlan;

        // An uploaded resume becomes a CV first; tailoring then runs on it.
        if (firstPlan.kind === 'import') {
          const importedCvId = cvId;
          input.onCvImported?.(importedCvId);
          const followUp = planAfterImport(input.state, importedCvId);
          if (followUp) {
            plan = followUp;
            cvId = await runPlan(followUp, input);
            // `generate` writes a brand-new row rather than updating the source,
            // so the import it was built from would linger as a second CV for a
            // resume the user uploaded once. A skill run avoids this with
            // `writeMode: 'update'`; this path has to clean up after itself.
            if (followUp.kind === 'generate' && cvId !== importedCvId) {
              await deleteTailoredCV(importedCvId).catch(() => undefined);
              input.onCvImported?.(cvId);
            }
          }
        }

        if (input.handoff?.recipientName || input.handoff?.recipientEmail) {
          await createOrUpdateCvHandoffDraft(cvId, {
            recipientName: input.handoff.recipientName.trim() || null,
            recipientEmail: input.handoff.recipientEmail.trim() || null,
            mode: input.handoff.mode,
          });
        }

        await queryClient.invalidateQueries({ queryKey: ['cvs'] });
        return { cvId, plan };
      } finally {
        setIsSubmitting(false);
      }
    },
    [queryClient, runPlan],
  );

  return { submit, isSubmitting, isGenerating };
}
